import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (cents) => `${((cents || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const PERIODS = [
  { id: 'all', label: 'Depuis le début' },
  { id: '7d', label: '7 derniers jours' },
  { id: '30d', label: '30 derniers jours' },
  { id: 'month', label: 'Ce mois' },
  { id: 'year', label: 'Cette année' },
];

const periodRange = (id) => {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (id === '7d') return { start: iso(new Date(now - 6 * 864e5)), end: iso(now) };
  if (id === '30d') return { start: iso(new Date(now - 29 * 864e5)), end: iso(now) };
  if (id === 'month') return { start: `${now.toISOString().slice(0, 7)}-01`, end: iso(now) };
  if (id === 'year') return { start: `${now.getFullYear()}-01-01`, end: iso(now) };
  return { start: '', end: '' };
};

const evol = (cur, prev) => {
  if (!prev) return null;
  return ((cur - prev) / prev) * 100;
};

function Evo({ pct, light }) {
  if (pct === null || !isFinite(pct)) return <span className={light ? 'text-gray-400' : 'text-white/30'}>—</span>;
  const up = pct >= 0;
  return <span className={`font-bold ${up ? 'text-green-400' : 'text-red-400'}`}>{up ? '+' : ''}{pct.toFixed(1)} %</span>;
}

function feePct(fee, gross) {
  if (!gross || !fee) return null;
  return ((fee / gross) * 100).toFixed(1);
}

function KpiCard({ label, t, light }) {
  const pct = feePct(t.fee, t.gross);
  return (
    <div className={`rounded-xl border p-4 ${light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
      <p className={`text-[10px] uppercase tracking-wide mb-1 ${light ? 'text-gray-500' : 'text-white/40'}`}>{label}</p>
      <p className={`text-lg font-bold leading-tight ${light ? 'text-gray-900' : 'text-white'}`}>{money(t.gross)} <span className={`text-[10px] font-semibold ${light ? 'text-gray-400' : 'text-white/40'}`}>BRUT</span></p>
      <p className="text-sm font-bold text-green-400">{money(t.net)} <span className="text-[10px] font-semibold opacity-60">NET</span></p>
      <p className={`text-[10px] mt-0.5 ${light ? 'text-gray-400' : 'text-white/30'}`}>{t.count} paiement(s) · frais {money(t.fee)}{pct ? <span className="font-bold text-amber-400"> ({pct} %)</span> : ''}</p>
    </div>
  );
}

export function RevenueTab({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [granularity, setGranularity] = useState('month');
  const [period, setPeriod] = useState('all');
  const [plan, setPlan] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [planSort, setPlanSort] = useState({ col: 'gross', asc: false });

  const load = useCallback(async () => {
    try {
      const { start, end } = periodRange(period);
      const { data: d } = await axios.get(`${API}/admin/revenue`, {
        params: { granularity, plan, start, end }, withCredentials: true,
      });
      setData(d);
      setSyncing(!!d.syncing);
    } catch { /* ignore */ }
  }, [granularity, period, plan]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [syncing, load]);

  const startSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/admin/revenue/sync`, {}, { withCredentials: true }); } catch { setSyncing(false); }
  };

  const chartData = useMemo(() => {
    let s = data?.series || [];
    if (granularity === 'day' && period === 'all') s = s.slice(-90);
    return s.map(r => ({ ...r, brut: r.gross / 100, net: r.net / 100 }));
  }, [data, granularity, period]);

  const planRows = useMemo(() => {
    const rows = [...(data?.by_plan || [])];
    rows.sort((a, b) => (planSort.asc ? 1 : -1) * ((a[planSort.col] || 0) - (b[planSort.col] || 0)));
    return rows;
  }, [data, planSort]);

  if (!data) return <div className="py-20 text-center text-xs text-white/40">Chargement des revenus…</div>;

  const card = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const tx = light ? 'text-gray-900' : 'text-white';
  const k = data.kpis;
  const sortIcon = (col) => planSort.col === col ? (planSort.asc ? <ChevronUp size={11} /> : <ChevronDown size={11} />) : null;
  const clickPlanSort = (col) => setPlanSort(s => ({ col, asc: s.col === col ? !s.asc : false }));

  return (
    <div className="space-y-5" data-testid="revenue-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className={`text-xs ${th}`}>
          {data.synced_at ? `${data.count} paiements Stripe · synchronisé le ${new Date(data.synced_at).toLocaleString('fr-FR')} · mise à jour en temps réel via webhook` : 'Aucune synchronisation — cliquez sur Synchroniser'}
          {data.sync_error && <span className="text-red-400"> · Erreur : {data.sync_error}</span>}
        </p>
        <button onClick={startSync} disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors disabled:opacity-60"
          data-testid="revenue-sync-btn">
          <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Synchronisation…' : 'Synchroniser les revenus'}
        </button>
      </div>

      {/* KPIs brut/net */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-kpis">
        <KpiCard label="Aujourd'hui" t={k.today} light={light} />
        <KpiCard label="Ce mois" t={k.this_month} light={light} />
        <KpiCard label="Cette année" t={k.this_year} light={light} />
        <KpiCard label="Depuis le début" t={k.all_time} light={light} />
      </div>

      {/* Moyennes */}
      {data.averages?.since && (
        <div className={`rounded-xl border p-4 flex flex-wrap gap-x-8 gap-y-2 items-center ${card}`} data-testid="revenue-averages">
          <span className={`text-[10px] uppercase tracking-wide font-bold ${th}`}>Moyennes (depuis le {new Date(data.averages.since).toLocaleDateString('fr-FR')})</span>
          {[['Par jour', data.averages.per_day], ['Par mois', data.averages.per_month], ['Par an', data.averages.per_year]].map(([l, v]) => (
            <span key={l} className="text-xs">
              <span className={th}>{l} : </span>
              <span className={`font-bold ${tx}`}>{money(v.gross)}</span>
              <span className={`text-[10px] ${th}`}> brut · </span>
              <span className="font-bold text-green-400">{money(v.net)}</span>
              <span className={`text-[10px] ${th}`}> net</span>
            </span>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className={`rounded-xl border p-4 ${card}`} data-testid="revenue-chart">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className={`text-sm font-bold ${tx}`}>Revenus {granularity === 'day' ? 'par jour' : granularity === 'month' ? 'par mois' : 'par an'} — brut & net</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {['day', 'month', 'year'].map(g => (
              <button key={g} onClick={() => setGranularity(g)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${granularity === g ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/50'}`}
                data-testid={`revenue-gran-${g}`}>{g === 'day' ? 'Jour' : g === 'month' ? 'Mois' : 'Année'}</button>
            ))}
            <select value={period} onChange={e => setPeriod(e.target.value)}
              className={`text-xs rounded-lg px-2 py-1.5 border focus:outline-none ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-white/5 border-white/10 text-white'}`}
              data-testid="revenue-period-select">
              {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select value={plan} onChange={e => setPlan(e.target.value)}
              className={`text-xs rounded-lg px-2 py-1.5 border focus:outline-none ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-white/5 border-white/10 text-white'}`}
              data-testid="revenue-plan-select">
              <option value="">Toutes les offres</option>
              {data.plan_labels.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={light ? '#e2e8f0' : 'rgba(255,255,255,0.06)'} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: light ? '#64748b' : 'rgba(255,255,255,0.4)' }} />
              <YAxis tick={{ fontSize: 10, fill: light ? '#64748b' : 'rgba(255,255,255,0.4)' }} tickFormatter={v => `${v} €`} width={70} />
              <ReTooltip formatter={(v, n) => [`${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`, n === 'brut' ? 'Brut' : 'Net']}
                contentStyle={{ background: light ? '#fff' : '#1a0e2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
              <Legend formatter={v => <span style={{ fontSize: 11, color: light ? '#334155' : 'rgba(255,255,255,0.7)' }}>{v === 'brut' ? 'Brut' : 'Net'}</span>} />
              <Bar dataKey="brut" fill="#E9641A" radius={[3, 3, 0, 0]} />
              <Bar dataKey="net" fill="#22c55e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Comparatif N / N-1 / N-2 / N-3 */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="revenue-comparisons">
        {[['Par année', data.comparisons.years], ['Même mois', data.comparisons.months], ['Même jour', data.comparisons.days]].map(([title, rows]) => (
          <div key={title} className={`rounded-xl border p-4 ${card}`}>
            <h3 className={`text-sm font-bold mb-2 ${tx}`}>{title} — N à N-3</h3>
            <table className="w-full text-left">
              <thead>
                <tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
                  {['Période', 'Brut', 'Net', 'Évol.'].map(h => <th key={h} className={`py-1.5 pr-2 text-[10px] font-semibold uppercase ${th}`}>{h}</th>)}
                </tr>
              </thead>
              <tbody className={light ? 'divide-y divide-gray-100' : 'divide-y divide-white/5'}>
                {rows.map((r, i) => (
                  <tr key={r.label}>
                    <td className={`py-1.5 pr-2 text-xs font-semibold whitespace-nowrap ${tx}`}>{r.label}{r.offset > 0 && <span className={`text-[9px] ml-1 ${th}`}>N-{r.offset}</span>}</td>
                    <td className={`py-1.5 pr-2 text-xs tabular-nums ${tx}`}>{money(r.gross)}</td>
                    <td className="py-1.5 pr-2 text-xs tabular-nums text-green-400">{money(r.net)}</td>
                    <td className="py-1.5 text-xs"><Evo pct={i + 1 < rows.length ? evol(r.gross, rows[i + 1].gross) : null} light={light} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Par abonnement */}
      <div className={`rounded-xl border p-4 ${card}`} data-testid="revenue-by-plan">
        <h3 className={`text-sm font-bold mb-2 ${tx}`}>Revenus par abonnement <span className={`text-[10px] font-normal ${th}`}>({PERIODS.find(p => p.id === period)?.label.toLowerCase()})</span></h3>
        <table className="w-full text-left">
          <thead>
            <tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
              <th className={`px-2 py-2 text-[10px] font-semibold uppercase ${th}`}>Offre</th>
              {[['count', 'Paiements'], ['gross', 'Brut'], ['fee', 'Frais Stripe'], ['net', 'Net'], ['share', 'Part']].map(([col, label]) => (
                <th key={col} onClick={() => clickPlanSort(col)}
                  className={`px-2 py-2 text-[10px] font-semibold uppercase cursor-pointer select-none hover:text-[#E9641A] ${planSort.col === col ? 'text-[#E9641A]' : th}`}
                  data-testid={`plan-sort-${col}`}>
                  <span className="inline-flex items-center gap-0.5">{label}{sortIcon(col)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={light ? 'divide-y divide-gray-100' : 'divide-y divide-white/5'}>
            {planRows.map(p => (
              <tr key={p.plan} data-testid={`plan-revenue-row-${p.plan}`}>
                <td className={`px-2 py-2 text-xs font-semibold ${tx}`}>{p.plan}</td>
                <td className={`px-2 py-2 text-xs tabular-nums ${light ? 'text-gray-600' : 'text-white/60'}`}>{p.count}</td>
                <td className={`px-2 py-2 text-xs tabular-nums font-bold ${tx}`}>{money(p.gross)}</td>
                <td className={`px-2 py-2 text-xs tabular-nums ${light ? 'text-gray-600' : 'text-white/60'}`}>{money(p.fee)}{feePct(p.fee, p.gross) ? <span className="text-[10px] font-bold text-amber-400"> ({feePct(p.fee, p.gross)} %)</span> : ''}</td>
                <td className="px-2 py-2 text-xs tabular-nums font-bold text-green-400">{money(p.net)}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2">
                    <div className={`h-1.5 w-24 rounded-full overflow-hidden ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                      <div className="h-full bg-[#E9641A] rounded-full" style={{ width: `${p.share}%` }} />
                    </div>
                    <span className={`text-[10px] font-bold ${tx}`}>{p.share} %</span>
                  </div>
                </td>
              </tr>
            ))}
            {planRows.length === 0 && <tr><td colSpan={6} className={`px-2 py-8 text-center text-xs ${th}`}>Aucune donnée — lancez une synchronisation</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
